import { useState } from 'react';
import { Check, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import type { DceProfile } from '../../types/dce-analysis.types';
import { useUpdateDceProfile } from '../../hooks/useDceAnalysis';
import { ConfidenceBadge } from './ConfidenceBadge';
import { SourceLink } from './SourceLink';

interface Props {
  profile: DceProfile;
  opportunityId: number;
}

export function ProfileCard({ profile, opportunityId }: Props) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(profile.humanNotes ?? '');
  const updateMutation = useUpdateDceProfile(opportunityId);

  const handleStatus = (humanStatus: 'VALIDATED' | 'REJECTED') => {
    updateMutation.mutate(
      { id: profile.id, payload: { humanStatus } },
      {
        onSuccess: () => toast.success(humanStatus === 'VALIDATED' ? 'Profil validé' : 'Profil rejeté'),
        onError: () => toast.error('Erreur lors de la mise à jour'),
      },
    );
  };

  const handleSaveNotes = () => {
    updateMutation.mutate(
      { id: profile.id, payload: { humanNotes: notesDraft } },
      {
        onSuccess: () => {
          setEditingNotes(false);
          toast.success('Notes enregistrées');
        },
        onError: () => toast.error('Erreur'),
      },
    );
  };

  return (
    <div className={`rounded-md border bg-card p-3 ${statusBg(profile.humanStatus)}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold">{profile.jobTitle}</h4>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {profile.seniority && (
              <Badge variant="outline" className="capitalize">{profile.seniority}</Badge>
            )}
            {profile.yearsExperienceMin !== null && (
              <Badge variant="outline">{profile.yearsExperienceMin}+ ans</Badge>
            )}
            <ConfidenceBadge confidence={profile.confidence} />
            <StatusBadge status={profile.humanStatus} />
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            size="sm"
            variant={profile.humanStatus === 'VALIDATED' ? 'default' : 'outline'}
            onClick={() => handleStatus('VALIDATED')}
            disabled={updateMutation.isPending}
            className="h-7"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={profile.humanStatus === 'REJECTED' ? 'destructive' : 'outline'}
            onClick={() => handleStatus('REJECTED')}
            disabled={updateMutation.isPending}
            className="h-7"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {profile.description && (
        <p className="text-sm text-muted-foreground mb-2">{profile.description}</p>
      )}

      {profile.technicalSkills.length > 0 && (
        <SkillsRow label="Compétences techniques" items={profile.technicalSkills} />
      )}
      {profile.functionalSkills.length > 0 && (
        <SkillsRow label="Compétences fonctionnelles" items={profile.functionalSkills} />
      )}
      {profile.certifications.length > 0 && (
        <SkillsRow label="Certifications" items={profile.certifications} />
      )}
      {profile.languages.length > 0 && (
        <SkillsRow label="Langues" items={profile.languages} />
      )}
      {profile.workUnits && (
        <div className="text-xs mt-2">
          <span className="font-medium">Unités d'œuvre :</span>{' '}
          <span className="text-muted-foreground">{profile.workUnits}</span>
        </div>
      )}

      <SourceLink
        documentId={profile.sourceDocumentId}
        filename={profile.sourceDocumentFilename}
        pageNumber={profile.sourcePageNumber}
        excerpt={profile.sourceExcerpt}
      />

      <div className="mt-2">
        {!editingNotes ? (
          <button
            type="button"
            onClick={() => setEditingNotes(true)}
            className="text-xs text-copper hover:underline flex items-center gap-1"
          >
            <Edit2 className="h-3 w-3" />
            {profile.humanNotes ? 'Modifier les notes' : 'Ajouter une note'}
          </button>
        ) : (
          <div className="space-y-1">
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Note interne pour ce profil…"
              className="text-xs"
              rows={2}
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={handleSaveNotes} className="h-7 text-xs">
                Enregistrer
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingNotes(false);
                  setNotesDraft(profile.humanNotes ?? '');
                }}
                className="h-7 text-xs"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}
        {profile.humanNotes && !editingNotes && (
          <p className="text-xs mt-1 italic text-muted-foreground">{profile.humanNotes}</p>
        )}
      </div>

      {profile.lastUpdatedByName && (
        <p className="text-[10px] text-muted-foreground mt-2">
          Modifié par {profile.lastUpdatedByName} · {formatRelative(profile.updatedAt)}
        </p>
      )}
    </div>
  );
}

function SkillsRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="mb-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {items.map((s, i) => (
          <span
            key={i}
            className="inline-block rounded bg-muted px-1.5 py-0.5 text-[11px]"
          >
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: DceProfile['humanStatus'] }) {
  if (status === 'EXTRACTED') return null;
  const label = { VALIDATED: 'Validé', REJECTED: 'Rejeté', CORRECTED: 'Corrigé' }[status];
  const color = {
    VALIDATED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    REJECTED: 'bg-red-100 text-red-700 border-red-300',
    CORRECTED: 'bg-blue-100 text-blue-700 border-blue-300',
  }[status];
  return <Badge variant="outline" className={color}>{label}</Badge>;
}

function statusBg(status: DceProfile['humanStatus']): string {
  if (status === 'VALIDATED') return 'border-emerald-300/50';
  if (status === 'REJECTED') return 'opacity-60';
  return '';
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR');
}
